import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { splitName, toDateInputValue } from '@/modules/service-desk/shared/application-utils';
import { useEmployeeSnapshot } from '@/modules/service-desk/shared/employee';
import { translatedResolver } from '@/modules/service-desk/shared/form-resolver';
import { ApplyFormPage } from '@/modules/service-desk/shared/components/apply-form-page';
import {
  FieldGrid,
  SelectField,
  TextareaField,
  TextField,
} from '@/modules/service-desk/shared/components/form-fields';
import { useNocApplication, useNocTypes, useSubmitNoc } from '../../hooks/use-noc';
import { NOC_COPY_TYPES, nocTypeRequirements } from '../../utils/noc-rules';
import { createNocApplySchema, NocApplyValues } from '../../schemas/noc-apply-form-schema';
import { NocType } from '../../types/noc.types';

const EMPTY_VALUES: NocApplyValues = {
  nocTypeId: '',
  nocCopyType: undefined as unknown as NocApplyValues['nocCopyType'],
  firstName: '',
  lastName: '',
  designation: '',
  passportNumber: '',
  startDate: '',
  endDate: '',
  travelDestination: '',
  reason: '',
};

export const NocApplyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id') ?? undefined;

  const { snapshot, isLoading: isSnapshotLoading } = useEmployeeSnapshot();
  const { data: existing, isLoading: isExistingLoading } = useNocApplication(applicationId);
  const { data: nocTypes = [] as NocType[], isLoading: isNocTypesLoading } = useNocTypes();
  const { mutateAsync: submit, isPending } = useSubmitNoc();

  // The validation rules depend on the chosen type, so the resolver reads the latest type list.
  const typesRef = useRef<NocType[]>(nocTypes);
  typesRef.current = nocTypes;

  const form = useForm<NocApplyValues>({
    resolver: (values, context, options) => {
      const typeName = typesRef.current.find((type) => type.ItemId === values.nocTypeId)?.Name;
      return translatedResolver(createNocApplySchema(nocTypeRequirements(typeName)), t)(
        values,
        context,
        options
      );
    },
    defaultValues: EMPTY_VALUES,
  });

  const startDate = form.watch('startDate');
  const selectedTypeId = form.watch('nocTypeId');
  const selectedType = nocTypes.find((type: NocType) => type.ItemId === selectedTypeId);
  const requirements = nocTypeRequirements(selectedType?.Name);

  useEffect(() => {
    if (applicationId) {
      if (!existing) return;
      const { firstName, lastName } = splitName(existing.Name);
      form.reset({
        nocTypeId: existing.NocTypeId ?? '',
        nocCopyType: existing.NocCopyType ?? EMPTY_VALUES.nocCopyType,
        firstName,
        lastName,
        designation: existing.Designation ?? '',
        passportNumber: existing.PassportNumber ?? '',
        startDate: toDateInputValue(existing.TravelStartDate),
        endDate: toDateInputValue(existing.TravelEndDate),
        travelDestination: existing.TravelDestination ?? '',
        reason: existing.Reason ?? '',
      });
      return;
    }
    if (!snapshot) return;
    const { firstName, lastName } = splitName(snapshot.displayName);
    form.reset({
      ...EMPTY_VALUES,
      firstName,
      lastName,
      designation: snapshot.designation,
      passportNumber: snapshot.passportNo,
    });
  }, [applicationId, existing, snapshot, form]);

  // A passport NOC is not for travel: the destination is cleared and no longer shown.
  useEffect(() => {
    if (requirements.isPassportType) form.setValue('travelDestination', '');
  }, [requirements.isPassportType, form]);

  const goBack = () => navigate('/hr/service-desk/noc');

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await submit({ existing: existing ?? undefined, values });
      goBack();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  });

  return (
    <ApplyFormPage
      form={form}
      titleKey="NOC_APPLICATION_FORM"
      isSubmitting={isPending}
      isEdit={Boolean(applicationId)}
      isLoading={applicationId ? isExistingLoading : isSnapshotLoading}
      // A new application needs the applicant's profile (prefill and department); an edit does not.
      submitDisabled={!applicationId && !snapshot}
      onCancel={goBack}
      onSubmit={handleSubmit}
      notice={
        <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t('NOC_FORM_DISCLAIMER')}
        </p>
      }
    >
      <FieldGrid>
        <SelectField
          control={form.control}
          name="nocTypeId"
          required
          label={t('NOC_TYPE')}
          options={nocTypes.map((type: NocType) => ({ value: type.ItemId, label: type.Name }))}
          isLoading={isNocTypesLoading}
        />
        <SelectField
          control={form.control}
          name="nocCopyType"
          required
          label={t('NOC_COPY_TYPE')}
          options={NOC_COPY_TYPES.filter(
            // "Both" is no longer offered (legacy business request); an old application keeps it.
            (option) => option.value !== 'Booth' || existing?.NocCopyType === 'Booth'
          ).map((option) => ({
            value: option.value,
            label: t(option.labelKey),
          }))}
        />
        <TextField control={form.control} name="firstName" required label={t('FIRST_NAME')} />
        <TextField control={form.control} name="lastName" required label={t('LAST_NAME')} />
        <TextField control={form.control} name="designation" required label={t('DESIGNATION')} />
        <TextField
          control={form.control}
          name="passportNumber"
          required={requirements.passportRequired}
          label={t('PASSPORT_NO')}
          description={t('PASSPORT_INFO')}
        />
        <TextField
          control={form.control}
          name="startDate"
          required={requirements.datesRequired}
          type="date"
          label={t('TRAVEL_START_DATE')}
        />
        <TextField
          control={form.control}
          name="endDate"
          required={requirements.datesRequired}
          type="date"
          min={startDate}
          label={t('TRAVEL_END_DATE')}
        />
        {!requirements.isPassportType && (
          <TextField
            control={form.control}
            name="travelDestination"
            required={requirements.destinationRequired}
            label={t('TRAVEL_DESTINATION')}
            placeholder={t('TYPE_TRAVEL_DESTINATION')}
            description={t('TRAVEL_DESTINATION_HINT')}
          />
        )}
      </FieldGrid>

      <TextareaField
        control={form.control}
        name="reason"
        required
        label={t('REASON')}
        description={t('NOC_DISCLAIMER')}
        rows={4}
      />
    </ApplyFormPage>
  );
};
